/**
 * `ExportGtwinAction` (WP-1).
 *
 * Header / Share-cluster button that builds and downloads the project's
 * `.gtwin` thin archive. Surfaces progress, file size, and checksum
 * verification per masterplan §5.4.
 *
 * Implementation notes:
 *   - Routes through `client.package.exportGtwin` (WP-1 adapter; mock path
 *     by default until the platform `POST /api/v1/gtwins/{id}/package/export`
 *     endpoint lands per `SCENE_ITEMS_SDK_SPEC.md` §2.3).
 *   - Feature-detect: if no `package.exportGtwin` method is present at
 *     runtime (older client surface), the button is rendered disabled
 *     with an explanatory tooltip instead of failing on click.
 *   - Token-only styling. Material Symbols `ios_share` (export) and
 *     `check_circle` (verified) only.
 */

import { useState } from 'react';
import { Banner, Icon, tokens } from '@/design';
import type { PackageAdapter, PackageExportResult } from '@/sdk-stub/gtwin-sdk';
import { useSyncStatusStore } from '@/state/useSyncStatusStore';

export interface ExportGtwinActionProps {
  readonly gtwinId: string;
  readonly projectLabel?: string;
  /** Adapter call. Pre-bound to `gtwinClient.package.exportGtwin` by caller. */
  readonly packageAdapter: PackageAdapter | null | undefined;
  /** Optional override for the mode advertised to the user. */
  readonly mode?: 'thin' | 'fat';
  /** Shorter button label for dense card footers. */
  readonly compact?: boolean;
  /** Optional human-readable note when the SDK surface isn't available. */
  readonly unsupportedReason?: string;
  readonly onExportError?: (message: string) => void;
  readonly 'data-testid'?: string;
}

interface ExportProgress {
  readonly received: number;
  readonly total: number | null;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes.toFixed(0)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function shortHash(sha256?: string): string {
  if (!sha256) return '—';
  return `${sha256.slice(0, 8)}…${sha256.slice(-4)}`;
}

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[2],
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  borderRadius: tokens.radius.input,
  border: tokens.border.thin,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.size.sm,
  cursor: 'pointer',
  minHeight: tokens.density.touchMin,
};

export function ExportGtwinAction({
  gtwinId,
  projectLabel,
  packageAdapter,
  mode = 'thin',
  compact = false,
  unsupportedReason,
  onExportError,
  'data-testid': testId,
}: ExportGtwinActionProps) {
  const supported = Boolean(packageAdapter && typeof packageAdapter.exportGtwin === 'function');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [result, setResult] = useState<PackageExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleExport() {
    if (!packageAdapter || !supported || busy) return;
    setBusy(true);
    setProgress({ received: 0, total: null });
    setResult(null);
    setError(null);
    try {
      const out = await packageAdapter.exportGtwin(gtwinId, {
        mode,
        onProgress: (received, total) => setProgress({ received, total }),
      });
      setResult(out);

      if (out.blob && typeof document !== 'undefined') {
        const objectUrl = URL.createObjectURL(out.blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${projectLabel ?? gtwinId}.gtwin.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } else if (out.url && typeof window !== 'undefined' && !out.url.startsWith('mock://')) {
        window.open(out.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      onExportError?.(message);
      // WP-14 — classify typed real-backend errors so the global
      // `<RealBackendBanner>` lights up alongside the inline error
      // pill.
      useSyncStatusStore.getState().recordRealBackendError(err);
    } finally {
      setBusy(false);
    }
  }

  const label = busy
    ? 'Exporting…'
    : compact
      ? `Export ${mode}`
      : `Export .gtwin (${mode})`;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[2],
        flex: compact ? '1 1 auto' : undefined,
        minWidth: 0,
      }}
      data-testid={testId ?? 'export-gtwin-action'}
    >
      <button
        type="button"
        onClick={handleExport}
        disabled={!supported || busy}
        aria-label={`Export .gtwin (${mode}) for ${projectLabel ?? gtwinId}`}
        title={
          supported
            ? `Build and download a ${mode} .gtwin archive.`
            : unsupportedReason
              ?? 'Export is not available against the current platform SDK build (coming soon).'
        }
        style={{
          ...buttonStyle,
          opacity: !supported || busy ? 0.6 : 1,
          cursor: !supported || busy ? 'not-allowed' : 'pointer',
          justifyContent: 'center',
          width: compact ? '100%' : undefined,
          boxSizing: 'border-box',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon name="ios_share" size={20} decorative />
        <span>{label}</span>
      </button>

      {progress && busy ? (
        <span
          role="status"
          aria-live="polite"
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.role.engMeta.size,
            fontFamily: tokens.font.family.mono,
          }}
        >
          {progress.total
            ? `${formatBytes(progress.received)} / ${formatBytes(progress.total)}`
            : `${formatBytes(progress.received)} streamed`}
        </span>
      ) : null}

      {result ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            color: tokens.color.semantic.success,
            fontSize: tokens.font.size.xs,
            fontFamily: tokens.font.family.ui,
          }}
        >
          <Icon name="check_circle" size={16} decorative />
          <span>
            {formatBytes(result.bytes)} ·{' '}
            <code style={{ fontFamily: tokens.font.family.mono }}>
              sha256: {shortHash(result.sha256)}
            </code>
            {result.checksumVerified ? ' · verified' : ''}
          </span>
        </div>
      ) : null}

      {error ? (
        <Banner
          severity="error"
          title="Export failed"
          body={error}
          errorCode="WP1-EXPORT-GTWIN"
        />
      ) : null}

      {!supported ? (
        <span
          style={{
            color: tokens.color.ink.muted,
            fontSize: tokens.font.role.engMeta.size,
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
          }}
        >
          <Icon name="info" size={16} decorative />
          {unsupportedReason
            ?? 'Export is gated until the platform SDK ships package.exportGtwin (SCENE_ITEMS_SDK_SPEC.md §2.3).'}
        </span>
      ) : null}
    </div>
  );
}
